/**
 * An org's own defect type, added while a word is mapped (POST /v1/twin/defect-types, migration 0265; the server is
 * platform-core/internal/twin/org_defect_types.go). A plant's scrap can book a word the pack has no defect for: a
 * person names the defect, says where in the route it is usually caused and, if they know it, which of the pack's
 * defect families it belongs to, and the word is bound to it. With a family, the analysis tests it with that family's
 * priors; with none, it is tested with no prior. Pure, so each rule is tested on its own. No dash is used as
 * punctuation in any wording.
 */
import type { CoverageWord } from "@/lib/api/import-coverage";
import { ApiClientError } from "@/lib/api-client";
import type {
  DefectSeverity,
  DefectTypeChoice,
  OrgDefectType,
  OrgDefectTypeInput,
  VocabBindingInput,
} from "@/lib/api/twin-vocabulary";

/** The form's fields as a person fills them in; "" is not chosen. */
export interface DefectTypeDraft {
  name: string;
  area: string;
  family: string;
  severity: "" | DefectSeverity;
}

/** The areas and families a new type may name (the targets' `areas` and `families`). */
export interface DefectTypeChoices {
  areas: readonly DefectTypeChoice[];
  families: readonly DefectTypeChoice[];
}

/** The choices from the targets, or null when the server sent none (an older server): no new type is offered then. */
export function defectTypeChoicesOf(
  t: { areas?: DefectTypeChoice[]; families?: DefectTypeChoice[] } | null | undefined,
): DefectTypeChoices | null {
  const areas = (t?.areas ?? []).filter((a) => a?.key);
  if (areas.length === 0) return null;
  return { areas, families: (t?.families ?? []).filter((f) => f?.key) };
}

/** A new draft for a word: named as the plant books it, with no area chosen yet and no family. */
export function newDefectTypeDraft(word: string): DefectTypeDraft {
  return { name: word.trim(), area: "", family: "", severity: "" };
}

/** The severities a type may carry, in words. */
export const SEVERITY_CHOICES: readonly { value: "" | DefectSeverity; label: string }[] = [
  { value: "", label: "Not set" },
  { value: "minor", label: "Minor: no real effect on use" },
  { value: "major", label: "Major: likely to fail in use" },
  { value: "critical", label: "Critical: unsafe, or the part cannot do its job" },
];

/** The input a draft saves, or why it cannot be saved yet. */
export function defectTypeInputOf(d: DefectTypeDraft): { input: OrgDefectTypeInput } | { error: string } {
  const name = d.name.trim().replace(/\s+/g, " ");
  if (!name) return { error: "Give the defect type a name." };
  if (!/[a-z0-9]/i.test(name)) return { error: "The name needs at least one letter from a to z or a digit." };
  if (!d.area) return { error: "Choose where in the route the defect is usually caused." };
  const input: OrgDefectTypeInput = { name, area: d.area };
  if (d.family) input.family = d.family;
  if (d.severity) input.severity = d.severity;
  return { input };
}

/** What the family means for the analysis, in a sentence: its priors, or none. */
export function familyNoteOf(family: string, families: readonly DefectTypeChoice[]): string {
  if (!family) {
    return (
      "No family: the analysis still tests this defect against the process, but without a prior, so it expects " +
      "nothing in advance and reads every link it finds as one to check."
    );
  }
  const label = families.find((f) => f.key === family)?.label ?? family.replace(/_/g, " ");
  return `The analysis tests this defect as one of ${label.toLowerCase()}, with the priors written for that family.`;
}

/** An API failure in the platform's words, else the fallback. */
function messageOf(err: unknown, fallback: string): string {
  if (err instanceof ApiClientError) {
    const body = err.body as { error?: string; detail?: string; message?: string } | null;
    return body?.detail || body?.message || body?.error || fallback;
  }
  return err instanceof Error && err.message ? err.message : fallback;
}

/** The approved binding of a word to a type, as the Files page saves it. */
export function typeBindingOf(w: CoverageWord, t: OrgDefectType): VocabBindingInput {
  return {
    kind: "defect",
    source_name: w.word,
    scope: "bound",
    target: t.key,
    status: "approved",
    reason: "Mapped on the Files page to a defect type the org added for it.",
    evidence: { from: "files_page", pieces: w.pieces, new_defect_type: t.key },
  };
}

/** What adding a type and mapping a word to it needs from the page. */
export interface CreateAndMapDeps {
  create: (input: OrgDefectTypeInput) => Promise<{ item: OrgDefectType }>;
  save: (bindings: VocabBindingInput[]) => Promise<unknown>;
  invalidate: () => void;
  notify: (kind: "ok" | "error", message: string) => void;
}

/** What became of the flow: the type when it was added, and whether the word was mapped to it. */
export interface CreateAndMapResult {
  created: OrgDefectType | null;
  mapped: boolean;
}

/**
 * What the page says when the type was added and the word's mapping to it failed. A word with the type's own key (the
 * type was named as the plant books the word) already reads as the type with no mapping, so it leaves the list of
 * words not mapped yet: the page says so, and that the type is now in every word's list of defects. Any other word stays
 * in the list with the type chosen in its row, to map with its Map button.
 */
export function mapFailedText(w: CoverageWord, created: OrgDefectType, reason: string): string {
  const added = `The defect type ${created.name} was added, but saving ${w.word} as it failed: ${reason}.`;
  if (w.key === created.key) {
    return (
      `${added} ${w.word} has the type's name, so it reads as ${created.name} anyway and leaves this list. ` +
      `${created.name} is now in every word's list of defects.`
    );
  }
  return `${added} ${created.name} is now chosen in the row of ${w.word}: press Map to save it.`;
}

/**
 * Adds the org's own defect type, then maps the word to it, in that order. When the type is refused nothing is saved
 * and the page says why. When the type is added and the mapping fails, the type stays, and the page says where the
 * word now stands (mapFailedText). Either way the page reads its data again once the type exists.
 */
export async function createTypeAndMap(
  w: CoverageWord,
  draft: DefectTypeDraft,
  deps: CreateAndMapDeps,
): Promise<CreateAndMapResult> {
  const checked = defectTypeInputOf(draft);
  if ("error" in checked) {
    deps.notify("error", `${w.word}: ${checked.error}`);
    return { created: null, mapped: false };
  }
  let created: OrgDefectType;
  try {
    created = (await deps.create(checked.input)).item;
  } catch (err) {
    deps.notify("error", `${w.word}: the defect type was not added: ${messageOf(err, "the server refused it")}`);
    return { created: null, mapped: false };
  }
  try {
    await deps.save([typeBindingOf(w, created)]);
  } catch (err) {
    deps.invalidate();
    deps.notify("error", mapFailedText(w, created, messageOf(err, "the word could not be mapped")));
    return { created, mapped: false };
  }
  deps.invalidate();
  deps.notify("ok", `${w.word} now reads as ${created.name}, the org's own defect type.`);
  return { created, mapped: true };
}

/** What adding a type for a word of a draft under review needs: the word's choice is the review's, saved on approval. */
export interface CreateForReviewDeps {
  create: (input: OrgDefectTypeInput) => Promise<{ item: OrgDefectType }>;
  /** Sets the word's choice to the new type. */
  choose: (key: string) => void;
  notify: (kind: "ok" | "error", message: string) => void;
}

/**
 * Adds the org's own defect type for a word of a draft under review and chooses it for the word. The review saves its
 * words when the draft is approved, so the word is bound then, with the others; a type refused changes nothing.
 * Returns the type, or null.
 */
export async function createTypeForReview(
  word: string,
  draft: DefectTypeDraft,
  deps: CreateForReviewDeps,
): Promise<OrgDefectType | null> {
  const checked = defectTypeInputOf(draft);
  if ("error" in checked) {
    deps.notify("error", `${word}: ${checked.error}`);
    return null;
  }
  try {
    const { item } = await deps.create(checked.input);
    deps.choose(item.key);
    deps.notify("ok", `The defect type ${item.name} was added. ${word} means it once you approve the draft.`);
    return item;
  } catch (err) {
    deps.notify("error", `${word}: the defect type was not added: ${messageOf(err, "the server refused it")}`);
    return null;
  }
}
